import { YearMonthDay } from './types';
import { DateTimePickerDropdownConfig } from '../types';
import { constructDate, currentDate, isBefore } from '@ardoq/date-time';

export function getDateFromYearMonthAndDay({ year, month, day }: YearMonthDay) {
  const date = currentDate();
  date.setFullYear(year, month, day);
  return date;
}

type WeekDays = number[];

/**
 * Gets the weeks of a month as arrays of dates in that week.
 * The days belonging to the previous or next month are filled with 0.
 *
 * E.g. getWeeksOfMonth(2017, 10) returns
 *
 * ```javascript
 *   [
 *     [0, 0, 1, 2, 3, 4, 5],
 *     [6, 7, 8, 9, 10, 11, 12],
 *     [13, 14, 15, 16, 17, 18, 19],
 *     [20, 21, 22, 23, 24, 25, 26],
 *     [27, 28, 29, 30, 0, 0, 0]
 *   ]
 * ```
 *
 * @param {number} year - The year.
 * @param {number} month - The month.
 * @param {boolean} [startWithMonday=true] - A boolean flag.
 * @return {Array} List of the weeks as arrays of dates in that week.
 */
export function getWeeksOfMonth(
  year: number,
  month: number,
  startWithMonday = true
) {
  let day = 1;
  const date = constructDate(year, month, day);
  let startDay = (date.getDay() + (startWithMonday ? 6 : 0)) % 7;
  let dayCount = 28;
  while (true) {
    date.setDate(dayCount + 1);
    if (date.getMonth() !== month) {
      break;
    }
    dayCount++;
  }

  const weeks: WeekDays[] = [];
  let week: WeekDays = [];
  while (startDay-- > 0) {
    week.push(0);
  }

  date.setFullYear(year);
  date.setMonth(month);
  while (day <= dayCount) {
    date.setDate(day);
    week.push(day++);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }

  if (week.length) {
    while (week.length < 7) {
      week.push(0);
    }
    weeks.push(week);
  }

  return weeks;
}

export const isDateAllowed = (
  date: Date,
  { minDate, maxDate }: DateTimePickerDropdownConfig
) =>
  !(
    (minDate && isBefore(date, minDate)) ||
    (maxDate && !isBefore(date, maxDate))
  );
